import { useContentHubEvents } from "../hooks";
import { flattenContentHubPages } from "../pagination";
import { type ContentHubSource, CONTENT_HUB_SOURCES } from "../config";
import type { ContentHubItem, TruthSocialPayload } from "../types";

function formatTimestamp(iso: string): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function itemUrl(item: ContentHubItem): string | null {
  if (item.backendSource !== CONTENT_HUB_SOURCES.TRUTH_SOCIAL) return null;
  const url = (item.raw as Partial<TruthSocialPayload> | null)?.url;
  return typeof url === "string" && url ? url : null;
}

function FeedRow({ item }: { item: ContentHubItem }) {
  const url = itemUrl(item);
  return (
    <li className="px-3 py-2 border-b border-gray-200 dark:border-gray-800">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="hover:underline"
            >
              {item.title}
            </a>
          ) : (
            item.title
          )}
        </span>
        <time className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
          {formatTimestamp(item.occursAt)}
        </time>
      </div>
      {item.body && (
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-300 whitespace-pre-line">
          {item.body}
        </p>
      )}
      {item.tickers.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {item.tickers.map((ticker) => (
            <span
              key={ticker}
              className="rounded bg-gray-200 dark:bg-gray-800 px-1.5 py-0.5 text-[10px] font-mono text-gray-700 dark:text-gray-300"
            >
              {ticker}
            </span>
          ))}
        </div>
      )}
    </li>
  );
}

/** Newest-first list of content-hub events for one source. */
export function ContentHubFeedList({
  source,
  title,
}: {
  source: ContentHubSource;
  title: string;
}) {
  const { data, isPending, isError, error } = useContentHubEvents(source);
  const items = flattenContentHubPages(data);

  return (
    <section className="flex flex-col min-h-0 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <header className="px-3 py-2 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h2>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isPending && (
          <p className="p-3 text-sm text-gray-500 dark:text-gray-400 animate-pulse">
            Loading…
          </p>
        )}
        {isError && (
          <p className="p-3 text-sm text-red-600 dark:text-red-400">
            Failed to load: {error instanceof Error ? error.message : "unknown error"}
          </p>
        )}
        {data && items.length === 0 && (
          <p className="p-3 text-sm text-gray-500 dark:text-gray-400">
            No events.
          </p>
        )}
        {items.length > 0 && (
          <ul>
            {items.map((item) => (
              <FeedRow key={item.publishId} item={item} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
